// packages/shared/src/briefing-link.ts
//
// KAN-482 F8 — the briefing-email click contract, in one place.
//
// `briefing:email_clicked` sat in the KAN-32 taxonomy with no trigger, because
// there was not a single `utm_` occurrence anywhere in the estate: no tagged
// link existed, so the event could never fire. This module is the trigger's
// contract. The sender (`apps/email`) builds tags with `buildBriefingLink`; the
// receiver (`apps/web`) reads them with `parseBriefingClick`. One module, so the
// two sides cannot drift — a tag builder in the email app and an ad-hoc parser
// in the web app would agree on the day they were written and silently diverge
// afterwards, and the only symptom would be an event that quietly stopped
// firing.
//
// It lives in `@intelhub/shared` rather than `@intelhub/analytics` for a build
// reason, not a design one: `apps/email` does not depend on the analytics
// package, and adding that dependency would churn `pnpm-lock.yaml` while four
// other KAN-32 lanes are in flight. `@intelhub/shared` is already a dependency
// of both apps. This module is deliberately pure — no DB, no drizzle, no I/O —
// so it is safe in a browser bundle via the `./briefing-link` subpath export.
//
// WHAT MAY TRAVEL IN A TAG. Five standard UTM params, all of them constants
// except the send's cohort date and the CTA slot. Nothing recipient-specific.
// That is enforced, not merely intended: `buildBriefingLink` throws on a base
// URL or path carrying a query string, a fragment, or an `@`.
//
// WHAT IS TRANSMITTED TO POSTHOG. Exactly one property, `briefing_date_offset`.
// The cohort date is never sent. See `bucketBriefingDateOffset` for why the top
// bucket is a cap rather than a band start.
//
// KAN-661 ADDS ONE CONTROLLED FRAGMENT. The clustered priority CTA carries an
// opaque, purpose-scoped briefing-context token to the story route. It arrives
// as its own typed argument and the builder writes the fragment itself; there is
// deliberately no "append an arbitrary fragment" API, because that would reopen
// the exact door the no-passenger rule closes on the query string. The token is
// minted and verified elsewhere — in the Node-only `briefing-context-token`
// module — so that this file stays free of key material and Node built-ins and
// remains safe in a browser bundle. Here the token is only ever an opaque
// string, checked for shape and length and nothing else.

/** `utm_source` on every briefing tag. Identifies the send, not the recipient. */
export const BRIEFING_UTM_SOURCE = 'intelhub-briefing'

/** `utm_medium` on every briefing tag. */
export const BRIEFING_UTM_MEDIUM = 'email'

/**
 * `utm_campaign` is `<prefix>-<cohortDate>`, e.g. `daily-briefing-2026-07-26`.
 *
 * The date rides in the campaign rather than in a bespoke param so the tag stays
 * five ordinary UTM keys — readable by any analytics tool, and obviously not a
 * tracking identifier to anyone who inspects the link. It is a cohort value:
 * every recipient of that morning's send carries the same one.
 */
export const BRIEFING_CAMPAIGN_PREFIX = 'daily-briefing'

/**
 * CTA slots that are tagged. Only links landing INSIDE IntelHub appear here.
 *
 * The per-item "Read More" buttons point at third-party publishers and are
 * deliberately absent: tagging them would hand our campaign structure to those
 * publishers and could never produce a click into our own app, which is the
 * only thing this event measures.
 */
export const BRIEFING_CTA_SLOTS = ['open-app', 'all-stories', 'priority-story'] as const
export type BriefingCtaSlot = (typeof BRIEFING_CTA_SLOTS)[number]

/**
 * The only slot permitted to carry a briefing-context token (KAN-661 R11).
 *
 * Scoping the token to one slot is what keeps the vocabulary closed. If any slot
 * could carry one, "which links may carry context?" would become a question you
 * answer by reading every call site instead of by reading this constant.
 */
export const BRIEFING_CONTEXT_TOKEN_SLOT: BriefingCtaSlot = 'priority-story'

/**
 * The fragment key the priority CTA writes, e.g. `#briefing-context=<token>`.
 *
 * A fragment rather than a query param on purpose: fragments are not sent to the
 * server in the request line, do not appear in server access logs, and are
 * stripped from `document.referrer` by every browser. The web app removes it with
 * `history.replaceState` before telemetry initialises (R14) — this constant is
 * what pins the two sides to the same key.
 */
export const BRIEFING_CONTEXT_FRAGMENT_KEY = 'briefing-context'

/**
 * Hard ceiling on the encoded token, in characters.
 *
 * Generous against the real format (six dot-delimited fields, worst case ~170
 * characters) and tight enough that a link cannot be used as a smuggling channel
 * for a payload. It lives here rather than in the token module because it is a
 * property of the LINK — the bound both the minter and the builder must respect.
 */
export const BRIEFING_CONTEXT_TOKEN_MAX_LENGTH = 256

/**
 * The alphabet an opaque briefing-context token may use.
 *
 * Deliberately narrower than "anything URL-safe": no `%`, no `/`, no `&`, no
 * `#`, no `?`, no whitespace. Nothing in this set can terminate the fragment,
 * start another one, or survive a percent-decode as something else, so the
 * builder can append the token verbatim and the output stays byte-stable.
 */
const OPAQUE_TOKEN = /^[A-Za-z0-9._-]+$/

/**
 * Bucket edges for `briefing_date_offset`, mirroring
 * `buckets.briefing_date_offset` in the canonical event registry.
 *
 * The two are pinned to each other by a drift test in
 * `apps/web/src/lib/__tests__/briefing-attribution.test.tsx`, which is the only
 * place in the repo that can see both this module and `@intelhub/analytics`.
 */
export const BRIEFING_DATE_OFFSET_BUCKETS = [0, 1, 3, 7, 30] as const

/** The four keys a briefing tag writes, and the four the app strips on arrival. */
export const BRIEFING_UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
] as const

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const MS_PER_DAY = 86_400_000

export interface BriefingLinkInput {
  /** Origin of the IntelHub web app, e.g. `https://intel.cyberfind.io`. */
  baseUrl: string
  /** Absolute in-app path the CTA lands on, e.g. `/` or `/stories`. */
  path: string
  /** The send's cohort date, `YYYY-MM-DD`. Shared by every recipient. */
  cohortDate: string
  slot: BriefingCtaSlot
  /**
   * Opaque briefing-context token, valid only on `priority-story`.
   *
   * This is the ONLY way a fragment reaches the output. The value is treated as
   * opaque here: this module cannot and does not verify it — see
   * `@intelhub/shared/briefing-context-token` for what it actually binds.
   */
  contextToken?: string
}

/**
 * Builds a UTM-tagged link into the IntelHub web app.
 *
 * Deterministic: same input, byte-identical output, with the params always
 * emitted in the same order. That matters because email HTML is snapshot-tested
 * and because a link that changes shape between renders defeats any downstream
 * de-duplication.
 *
 * Throws rather than sanitising on bad input. A silently-corrected tag is worse
 * than no tag: it would ship a link that looks right and attributes wrongly.
 */
export function buildBriefingLink(input: BriefingLinkInput): string {
  const { baseUrl, path, cohortDate, slot, contextToken } = input

  if (!ISO_DATE.test(cohortDate)) {
    throw new Error(`buildBriefingLink: cohortDate must be YYYY-MM-DD, got "${cohortDate}"`)
  }
  if (!path.startsWith('/')) {
    throw new Error(`buildBriefingLink: path must be absolute, got "${path}"`)
  }
  if (!BRIEFING_CTA_SLOTS.includes(slot)) {
    throw new Error(`buildBriefingLink: unknown CTA slot "${slot}"`)
  }
  // The no-passenger rule. A query string or fragment on the incoming path is
  // the mechanism by which a recipient identifier would end up in a tagged
  // link — `unsubscribeUrl` already carries `?userId=…`, and it would take one
  // careless reuse to tag it. `@` catches a mailto: or userinfo-bearing base.
  for (const [label, value] of [['baseUrl', baseUrl], ['path', path]] as const) {
    if (/[?#@]/.test(value)) {
      throw new Error(
        `buildBriefingLink: ${label} must not carry a query, fragment or "@" — ` +
          'a tagged briefing link never carries recipient-specific data',
      )
    }
  }

  let url: URL
  try {
    url = new URL(path, baseUrl)
  } catch {
    throw new Error(`buildBriefingLink: baseUrl is not a valid absolute URL: "${baseUrl}"`)
  }

  url.searchParams.set('utm_source', BRIEFING_UTM_SOURCE)
  url.searchParams.set('utm_medium', BRIEFING_UTM_MEDIUM)
  url.searchParams.set('utm_campaign', `${BRIEFING_CAMPAIGN_PREFIX}-${cohortDate}`)
  url.searchParams.set('utm_content', slot)

  if (contextToken === undefined) return url.toString()

  // The token is appended by hand rather than through `url.hash`. The setter
  // percent-encodes some characters and leaves others alone depending on the
  // runtime's URL implementation; `OPAQUE_TOKEN` has already ruled out every
  // character that could differ, so a literal append is both byte-stable across
  // engines and obviously so to a reader.
  if (slot !== BRIEFING_CONTEXT_TOKEN_SLOT) {
    throw new Error(
      `buildBriefingLink: contextToken is only valid on the "${BRIEFING_CONTEXT_TOKEN_SLOT}" CTA slot, got "${slot}"`,
    )
  }
  if (
    typeof contextToken !== 'string' ||
    contextToken.length === 0 ||
    contextToken.length > BRIEFING_CONTEXT_TOKEN_MAX_LENGTH ||
    !OPAQUE_TOKEN.test(contextToken)
  ) {
    throw new Error(
      'buildBriefingLink: contextToken must be an opaque token of 1–' +
        `${BRIEFING_CONTEXT_TOKEN_MAX_LENGTH} characters from [A-Za-z0-9._-]`,
    )
  }

  return `${url.toString()}#${BRIEFING_CONTEXT_FRAGMENT_KEY}=${contextToken}`
}

/**
 * Whole days between a briefing's cohort date and a click, both taken as UTC
 * calendar days so a recipient's timezone never shifts the answer.
 *
 * Clamped at 0. A negative offset means clock skew or a hand-edited link, and
 * "-1 days" is not a fact about anybody's reading habits.
 */
export function briefingDateOffsetDays(cohortDate: string, now: Date): number {
  const cohort = Date.parse(`${cohortDate}T00:00:00Z`)
  if (Number.isNaN(cohort)) return 0
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  return Math.max(0, Math.floor((today - cohort) / MS_PER_DAY))
}

/**
 * Buckets a raw day offset down to the nearest edge in
 * `BRIEFING_DATE_OFFSET_BUCKETS`.
 *
 * The top edge is a CAP, not the start of an open band: everything at 30 days or
 * beyond reports 30. That is the point of the bucketing. The cohort date is
 * never transmitted precisely because it would let a stale click be tied back to
 * one specific morning's send — and an *unbounded* day count reconstructs that
 * date exactly, from the offset plus the ingest timestamp. A capped bucket
 * cannot.
 */
export function bucketBriefingDateOffset(rawDays: number): number {
  // NaN compares false against every edge, so the loop would silently return the
  // bottom band without saying so. Say so. `Infinity` needs no special case: it
  // is >= the top edge and the cap catches it, which is the right answer.
  if (Number.isNaN(rawDays)) return BRIEFING_DATE_OFFSET_BUCKETS[0]
  let band: number = BRIEFING_DATE_OFFSET_BUCKETS[0]
  for (const edge of BRIEFING_DATE_OFFSET_BUCKETS) if (rawDays >= edge) band = edge
  return band
}

/**
 * The exact property payload for `briefing:email_clicked`.
 *
 * Structurally identical to the generated `EventProperties['briefing:email_clicked']`
 * in `@intelhub/analytics`; the call site is type-checked against the generated
 * type, so a registry change that this shape no longer satisfies fails the build
 * rather than shipping a mismatched event.
 */
export interface BriefingClickAttribution {
  briefing_date_offset: number
}

/**
 * Reads a briefing tag off a landing URL's query string.
 *
 * Returns `null` — never a partial or defaulted result — unless the tag is
 * complete and well-formed: our source, our medium, and a campaign that parses
 * to `<prefix>-<YYYY-MM-DD>`. Any other traffic (organic, a hand-typed URL,
 * someone else's UTM tags) must not be counted as a briefing click, so failing
 * closed is the whole behaviour here.
 *
 * Note what is NOT returned: no URL, no query string, no `utm_*` value, no
 * cohort date. Only the bucketed offset ever leaves this function.
 */
export function parseBriefingClick(
  search: string,
  now: Date = new Date(),
): BriefingClickAttribution | null {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return null
  }

  if (params.get('utm_source') !== BRIEFING_UTM_SOURCE) return null
  if (params.get('utm_medium') !== BRIEFING_UTM_MEDIUM) return null

  const campaign = params.get('utm_campaign')
  if (!campaign || !campaign.startsWith(`${BRIEFING_CAMPAIGN_PREFIX}-`)) return null

  const cohortDate = campaign.slice(BRIEFING_CAMPAIGN_PREFIX.length + 1)
  if (!ISO_DATE.test(cohortDate)) return null
  // Rejects `2026-13-45`: the regex only proves the shape.
  if (!Number.isFinite(Date.parse(`${cohortDate}T00:00:00Z`))) return null

  return {
    briefing_date_offset: bucketBriefingDateOffset(briefingDateOffsetDays(cohortDate, now)),
  }
}

/**
 * Removes the four briefing UTM keys from a query string, preserving every other
 * param and their order.
 *
 * The web app rewrites its own URL with this immediately after capturing, so the
 * tag cannot survive into a later `$pageview`, `$referrer` or `document.referrer`
 * on an outbound link. R22's `sanitizeUrl` already reduces captured URLs to route
 * patterns, so this is defence in depth rather than the only barrier — but the
 * browser's own referrer header is outside R22's reach, and this is what covers it.
 *
 * Returns a leading `?` only when something survives, so a fully-stripped URL is
 * `/` and not `/?`.
 */
export function stripBriefingParams(search: string): string {
  const params = new URLSearchParams(search)
  for (const key of BRIEFING_UTM_KEYS) params.delete(key)
  const rest = params.toString()
  return rest ? `?${rest}` : ''
}
